import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTeam } from '../context/TeamContext';
import * as templatesApi from '../api/templates';
import type { Template } from '../api/templates';
import { ScheduleDaysPicker } from './ScheduleDaysPicker';

interface Props {
  open: boolean;
  template: Template | null;
  onClose: () => void;
  onDone: (toastMessage: string) => Promise<void> | void;
}

interface EditableItem {
  id?: string; // existing items keep their id so done-state doesn't wipe
  label: string;
}

/**
 * Edit an existing template (ADR 0010). Group membership is fixed — moving
 * a template between groups is deferred; create a new one instead.
 */
export function EditTemplateModal({ open, template, onClose, onDone }: Props) {
  const { members } = useTeam();
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<EditableItem[]>([]);
  const [labelDraft, setLabelDraft] = useState('');
  const [scheduleDays, setScheduleDays] = useState<number[]>([]);
  const [defaultAssigneeSub, setDefaultAssigneeSub] = useState<string>('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !template) return;
    setTitle(template.title);
    setDescription(template.description ?? '');
    setItems(
      template.checklistItems.map((i) => ({ id: i.id, label: i.label }))
    );
    setLabelDraft('');
    setScheduleDays([...template.scheduleDays]);
    setDefaultAssigneeSub(template.defaultAssigneeSub ?? '');
    setActive(template.active);
    setErr(null);
    setTimeout(() => firstFieldRef.current?.focus(), 0);
  }, [open, template]);

  if (!open || !template) return null;

  function addLabel() {
    const v = labelDraft.trim();
    if (!v) return;
    setItems((arr) => [...arr, { label: v }]);
    setLabelDraft('');
  }
  function removeLabel(idx: number) {
    setItems((arr) => arr.filter((_, i) => i !== idx));
  }
  function updateLabel(idx: number, value: string) {
    setItems((arr) => arr.map((i, j) => (j === idx ? { ...i, label: value } : i)));
  }

  const canSubmit = title.trim().length > 0 && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || !template) return;
    setBusy(true);
    setErr(null);
    try {
      await templatesApi.updateTemplate(template.groupId, template.templateId, {
        title: title.trim(),
        description: description.trim(),
        checklistItems: items
          .map((i) => ({ id: i.id, label: i.label.trim() }))
          .filter((i) => i.label.length > 0),
        scheduleDays,
        defaultAssigneeSub: defaultAssigneeSub || null,
        active,
      });
      await onDone(`Saved template "${title.trim()}"`);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="new-task-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Edit template"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form
        className="new-task-dialog settings-modal template-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <header>
          <h3>Edit template</h3>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>

        <label>
          <span>Title</span>
          <input
            ref={firstFieldRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </label>

        <label>
          <span>Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </label>

        {members.length > 1 && (
          <label>
            <span>Default assignee</span>
            <select
              value={defaultAssigneeSub}
              onChange={(e) => setDefaultAssigneeSub(e.target.value)}
            >
              <option value="">(creator)</option>
              {members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.displayName}
                </option>
              ))}
            </select>
          </label>
        )}

        <fieldset className="template-checklist-fieldset">
          <legend>Checklist items</legend>
          {items.map((it, i) => (
            <div key={it.id ?? `new-${i}`} className="template-checklist-row">
              <input
                type="text"
                value={it.label}
                onChange={(e) => updateLabel(i, e.target.value)}
              />
              <button
                type="button"
                className="ghost small"
                onClick={() => removeLabel(i)}
                aria-label="Remove item"
              >
                ×
              </button>
            </div>
          ))}
          <div className="template-checklist-row">
            <input
              type="text"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addLabel();
                }
              }}
              placeholder="New item…"
            />
            <button
              type="button"
              onClick={addLabel}
              disabled={!labelDraft.trim()}
            >
              + Add
            </button>
          </div>
        </fieldset>

        <fieldset className="template-schedule-fieldset">
          <legend>Schedule days</legend>
          <ScheduleDaysPicker
            value={scheduleDays}
            onChange={setScheduleDays}
          />
        </fieldset>

        <label className="new-task-private-row">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          <span>Active</span>
        </label>

        {err && <div className="settings-error">{err}</div>}

        <div className="new-task-footer">
          <span className="muted small">
            In {template.groupDisplayName ?? template.groupId}
          </span>
          <button type="submit" className="primary" disabled={!canSubmit}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
